import { PrimaryHeading } from '@ui-components/typography/headings/PrimaryHeading'
import * as React from 'react'
import { styled } from 'styled-components'

import { EmojiType } from '../assets/Emojis'
import { Colors } from '../global/colors'
import {
  BREAKPOINT_DESKTOP_PX,
  BREAKPOINT_LARGE_DESKTOP_PX,
  MAIN_CONTAINER_WIDTH_LARGE_DESKTOP_PX,
  SECTION_VERTICAL_PADDING_DEFAULT,
} from '../global/constants'
import { LEGACY_SPACE_XLARGE } from '../global/legacy/spacing'
import { SPACE_SMALL } from '../global/spacing'
import { TwoColumnLayout } from './TwoColumnLayout'

export interface AssistantProps {
  title: string
  emoji: EmojiType
  leadText?: React.ReactNode
  additionalText?: string
  children?: React.ReactNode
  twoColumnLayoutSupport?: boolean
  fullViewportHeight?: boolean
  ref?: React.Ref<HTMLDivElement>
  testId?: string
}

export const Assistant = (props: AssistantProps): React.ReactNode => {
  const {
    emoji,
    title,
    leadText,
    additionalText,
    children,
    twoColumnLayoutSupport = true,
    fullViewportHeight,
    testId,
    ...rest
  } = props

  const header = (
    <Header>
      <PrimaryHeading
        isInfoHeading={true}
        header={title}
        emoji={emoji}
        leadText={leadText}
        additionalText={additionalText}
      />
    </Header>
  )

  return (
    <Container $fullViewportHeight={fullViewportHeight} data-testid={testId} {...rest}>
      <ContentColumn>
        {twoColumnLayoutSupport ? (
          <TwoColumnLayout leftContent={header} rightContent={children} />
        ) : (
          <OneColumnLayout>
            {header}
            {children}
          </OneColumnLayout>
        )}
      </ContentColumn>
    </Container>
  )
}

const Container = styled.div<{ $fullViewportHeight?: boolean }>`
  background-color: ${Colors.BackgroundWeakerSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  color: ${Colors.TextStrongestPrimary}; // use same color for all child texts. Override in children if needed
  height: ${({ $fullViewportHeight }) => ($fullViewportHeight ? '100%' : undefined)};
  > :first-child {
    @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
      padding-top: ${({ $fullViewportHeight }) => ($fullViewportHeight ? '3rem' : undefined)};
      padding-bottom: ${({ $fullViewportHeight }) => ($fullViewportHeight ? '9rem' : undefined)};
    }
  }

  padding: ${SECTION_VERTICAL_PADDING_DEFAULT}px ${SPACE_SMALL};
  padding-bottom: ${LEGACY_SPACE_XLARGE};
`

const ContentColumn = styled.section`
  max-width: ${BREAKPOINT_DESKTOP_PX - 1}px;
  width: 100%;

  @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
    max-width: ${MAIN_CONTAINER_WIDTH_LARGE_DESKTOP_PX}px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-self: start;
  gap: ${SPACE_SMALL};
`

const OneColumnLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${LEGACY_SPACE_XLARGE};
`
